import React, { useState, useEffect, useMemo } from 'react';
import { collection, getDocs, Timestamp } from 'firebase/firestore';
import { db } from '../services/firebase';
import { FirebaseUser } from '../types/user';
import './stylesheets/InterviewAnalysis.css';
import SelectedInterviewModal from '../components/SelectedInterviewModal';
import InProgressInterviewsModal from '../components/InProgressInterviewsModal';

interface InterviewAttempt {
  id: string;
  answers: any[];
  average_score: number;
  created_at: string;
  duration_minutes: number;
  ended_at: string;
  question_count: number;
  sessionId: string;
  session_id: string;
  source: string;
  specialty: string;
  specialtyId: string;
  started_at: string;
  status: string;
  uid: string;
  updated_at: string;
  user_id: string;
}

const InterviewAnalysis: React.FC = () => {
  const [interviewAttempts, setInterviewAttempts] = useState<InterviewAttempt[]>([]);
  const [users, setUsers] = useState<FirebaseUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' }>({ 
    key: 'ended_at', 
    direction: 'desc' 
  });
  // Interview filter state
  type InterviewFilter = 'all' | 'completedToday' | 'completedLast7Days' | 'uniqueUsersLast7Days' | 'questionsLast7Days';
  const [interviewFilter, setInterviewFilter] = useState<InterviewFilter>('all');
  const [lastClickedBox, setLastClickedBox] = useState<InterviewFilter | null>(null);
  const [selectedInterview, setSelectedInterview] = useState<InterviewAttempt | null>(null);
  const [showInProgressModal, setShowInProgressModal] = useState(false);
  const [hideTestUsers, setHideTestUsers] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      // Load interview attempts
      const attemptsCollection = collection(db, 'interviewAttempts');
      const attemptsSnapshot = await getDocs(attemptsCollection);
      const attemptsList = attemptsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as InterviewAttempt[];
      
      // Load users
      const usersCollection = collection(db, 'users');
      const usersSnapshot = await getDocs(usersCollection);
      const usersList = usersSnapshot.docs.map(doc => ({
        uid: doc.id,
        ...doc.data()
      })) as FirebaseUser[];
      
      setInterviewAttempts(attemptsList);
      setUsers(usersList);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Helper function to parse date strings
  const parseDate = (dateString: string): Date => {
    return new Date(dateString);
  };

  // Helper function to get user name from UID
  const getUserName = (uid: string): string => {
    const user = users.find(u => u.uid === uid);
    return user?.name || user?.email || 'Unknown User';
  };

  // Table sorting and filtering
  const filteredAndSortedAttempts = useMemo(() => {
    let filtered = [...interviewAttempts];
    const now = new Date();
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);

    // Filter out test users if hideTestUsers is enabled
    if (hideTestUsers) {
      filtered = filtered.filter(attempt => {
        const userName = getUserName(attempt.user_id || attempt.uid);
        return userName !== 'Mock Medic Admin' && userName !== 'Test User 3';
      });
    }

    // Apply analytics card filter
    if (interviewFilter === 'completedToday') {
      filtered = filtered.filter(attempt => {
        if (attempt.status !== 'ended') return false;
        const endDate = parseDate(attempt.ended_at);
        return endDate >= todayStart && endDate <= now;
      });
    } else if (interviewFilter === 'completedLast7Days') {
      filtered = filtered.filter(attempt => {
        if (attempt.status !== 'ended') return false;
        const endDate = parseDate(attempt.ended_at);
        return endDate >= sevenDaysAgo && endDate <= now;
      });
    } else if (interviewFilter === 'uniqueUsersLast7Days') {
      // Only show one attempt per user in last 7 days
      const seen = new Set<string>();
      filtered = filtered.filter(attempt => {
        if (attempt.status !== 'ended') return false;
        const endDate = parseDate(attempt.ended_at);
        const userKey = attempt.user_id || attempt.uid;
        if (endDate >= sevenDaysAgo && endDate <= now && !seen.has(userKey)) {
          seen.add(userKey);
          return true;
        }
        return false;
      });
    } else if (interviewFilter === 'questionsLast7Days') {
      filtered = filtered.filter(attempt => {
        if (attempt.status !== 'ended') return false;
        const endDate = parseDate(attempt.ended_at);
        return endDate >= sevenDaysAgo && endDate <= now && attempt.question_count > 0;
      });
    }

    // Apply search filter
    const term = search.trim().toLowerCase();
    if (term) {
      filtered = filtered.filter(attempt => {
        const sessionId = attempt.sessionId || attempt.session_id || '';
        const userName = getUserName(attempt.user_id || attempt.uid);
        const specialty = attempt.specialty || '';
        const questionCount = String(attempt.question_count || 0);
        return [sessionId, userName, specialty, questionCount]
          .filter(Boolean)
          .some(val => String(val).toLowerCase().includes(term));
      });
    }

    // Apply sorting
    const sorted = filtered.sort((a, b) => {
      const { key, direction } = sortConfig;
      const dir = direction === 'asc' ? 1 : -1;

      let aVal: any = (a as any)[key];
      let bVal: any = (b as any)[key];

      // Handle date sorting
      if (key === 'started_at' || key === 'ended_at') {
        aVal = aVal ? parseDate(aVal).getTime() : 0;
        bVal = bVal ? parseDate(bVal).getTime() : 0;
      }

      // Handle user name sorting
      if (key === 'user_name') {
        aVal = getUserName(a.user_id || a.uid);
        bVal = getUserName(b.user_id || b.uid);
      }

      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return 1;
      if (bVal == null) return -1;

      return (aVal > bVal ? 1 : aVal < bVal ? -1 : 0) * dir;
    });

    return sorted;
  }, [interviewAttempts, users, search, sortConfig, interviewFilter, hideTestUsers]);

  const toggleSort = (key: string) => {
    setSortConfig(prev => {
      if (prev.key === key) {
        return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { key, direction: key === 'ended_at' ? 'desc' : 'asc' };
    });
  };

  const sortIndicator = (key: string) => {
    if (sortConfig.key !== key) return null;
    return sortConfig.direction === 'asc' ? ' ▲' : ' ▼';
  };

  // Calculate analytics
  const calculateAnalytics = () => {
    const now = new Date();
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);

    // Filter completed interviews (status: "ended")
    let completedAttempts = interviewAttempts.filter(attempt => attempt.status === 'ended');

    // Filter out test users if hideTestUsers is enabled
    if (hideTestUsers) {
      completedAttempts = completedAttempts.filter(attempt => {
        const userName = getUserName(attempt.user_id || attempt.uid);
        return userName !== 'Mock Medic Admin' && userName !== 'Test User 3';
      });
    }

    // 1. Number completed today
    const completedToday = completedAttempts.filter(attempt => {
      const endDate = parseDate(attempt.ended_at);
      return endDate >= todayStart && endDate <= now;
    }).length;

    // 2. Number completed in the last 7 days
    const completedLast7Days = completedAttempts.filter(attempt => {
      const endDate = parseDate(attempt.ended_at);
      return endDate >= sevenDaysAgo && endDate <= now;
    }).length;

    // 3. Number of different users completing an interview in the past 7 days
    const uniqueUsersLast7Days = new Set(
      completedAttempts
        .filter(attempt => {
          const endDate = parseDate(attempt.ended_at);
          return endDate >= sevenDaysAgo && endDate <= now;
        })
        .map(attempt => attempt.user_id || attempt.uid)
    ).size;

    // 4. Total number of questions answered in past 7 days
    const totalQuestionsLast7Days = completedAttempts
      .filter(attempt => {
        const endDate = parseDate(attempt.ended_at);
        return endDate >= sevenDaysAgo && endDate <= now;
      })
      .reduce((total, attempt) => total + (attempt.question_count || 0), 0);

    // 5. All time completed interviews
    const allTimeCompleted = completedAttempts.length;

    return {
      completedToday,
      completedLast7Days,
      uniqueUsersLast7Days,
      totalQuestionsLast7Days,
      allTimeCompleted
    };
  };

  const analytics = useMemo(() => calculateAnalytics(), [interviewAttempts, users, hideTestUsers]);

  // Helper function to get selected user for modal
  const getSelectedUser = (): FirebaseUser | null => {
    if (!selectedInterview) return null;
    const userId = selectedInterview.user_id || selectedInterview.uid;
    return users.find(u => u.uid === userId) || null;
  };

  return (
    <div className="analysis-page" style={selectedInterview ? {overflow: 'hidden', height: '100vh'} : {}}>
      <h2>Interview Analysis</h2>
      
      {/* Analytics Cards */}
      <div className="analytics-grid" style={{marginBottom: '30px'}}>
        <div
          className={`analytics-card${interviewFilter==='completedToday' ? ' active' : ''}`}
          style={{cursor:'pointer', border: interviewFilter==='completedToday' ? '2px solid #64b5f6' : undefined}}
          onClick={() => {
            setInterviewFilter('completedToday');
            setLastClickedBox('completedToday');
          }}
        >
          <h3>Completed Today</h3>
          <div className="analytics-number">{analytics.completedToday}</div>
        </div>
        <div
          className={`analytics-card${interviewFilter==='completedLast7Days' ? ' active' : ''}`}
          style={{cursor:'pointer', border: interviewFilter==='completedLast7Days' ? '2px solid #64b5f6' : undefined}}
          onClick={() => {
            setInterviewFilter('completedLast7Days');
            setLastClickedBox('completedLast7Days');
          }}
        >
          <h3>Completed Last 7 Days</h3>
          <div className="analytics-number">{analytics.completedLast7Days}</div>
        </div>
        <div
          className={`analytics-card${interviewFilter==='all' ? ' active' : ''}`}
          style={{cursor:'pointer', border: interviewFilter==='all' ? '2px solid #64b5f6' : undefined}}
          onClick={() => {
            setInterviewFilter('all');
            setLastClickedBox('all');
          }}
        >
          <h3>All Completed Interviews</h3>
          <div className="analytics-number">{analytics.allTimeCompleted}</div>
        </div>
        <div
          className="analytics-card"
        >
          <h3>Active Users (7 Days)</h3>
          <div className="analytics-number">{analytics.uniqueUsersLast7Days}</div>
        </div>
        <div
          className="analytics-card"
        >
          <h3>Questions Answered (7 Days)</h3>
          <div className="analytics-number">{analytics.totalQuestionsLast7Days}</div>
        </div>
      </div>

      {/* Action Buttons */}
      <div style={{
        display: 'flex', 
        justifyContent: 'flex-end', 
        marginBottom: '20px',
        gap: '12px',
        flexWrap: 'wrap'
      }}>
        <button
          onClick={() => setHideTestUsers(!hideTestUsers)}
          style={{
            background: hideTestUsers ? '#ff9800' : '#2196f3',
            color: 'white',
            border: 'none',
            padding: '12px 20px',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '600',
            transition: 'all 0.3s ease',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = hideTestUsers ? '#f57c00' : '#1976d2';
            e.currentTarget.style.transform = 'translateY(-1px)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = hideTestUsers ? '#ff9800' : '#2196f3';
            e.currentTarget.style.transform = 'translateY(0)';
          }}
        >
          <span>{hideTestUsers ? '👁️' : '🚫'}</span>
          {hideTestUsers ? 'Show Test Users' : 'Hide Test Users'}
        </button>
        <button
          onClick={() => setShowInProgressModal(true)}
          style={{
            background: '#4caf50',
            color: 'white',
            border: 'none',
            padding: '12px 20px',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '600',
            transition: 'all 0.3s ease',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = '#45a049';
            e.currentTarget.style.transform = 'translateY(-1px)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = '#4caf50';
            e.currentTarget.style.transform = 'translateY(0)';
          }}
        >
          <span>⏱️</span>
          View In-Progress Interviews
        </button>
      </div>

      {loading ? (
        <div className="loading">Loading interview data...</div>
      ) : (
        <div className="attempts-table-wrapper">
          <div style={{marginBottom: '16px', display:'flex', flexWrap:'wrap', gap:'12px', alignItems:'center'}}>
            <input
              type="text"
              placeholder="Search by session ID, user name or specialty..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{
                padding: '10px 14px',
                borderRadius: 6,
                border: '5px solid #949494ff',
                background: '#585757ff',
                color: '#f3f3f3ff',
                flex: '1 1 260px'
              }}
            />
            {search && (
              <button
                onClick={() => setSearch('')}
                style={{
                  background:'#444',
                  color:'#eee',
                  border:'1px solid #555',
                  padding:'10px 16px',
                  borderRadius:6,
                  cursor:'pointer'
                }}
              >Clear</button>
            )}
            <span style={{marginLeft:'auto', color:'#64b5f6', fontWeight:600, fontSize:'16px', paddingRight:'18px'}}>
              {filteredAndSortedAttempts.length} result{filteredAndSortedAttempts.length === 1 ? '' : 's'}
            </span>
          </div>
          
          <div className="attempts-table-container">
            <table className="attempts-table">
              <thead>
                <tr>
                  <th style={{cursor:'pointer'}} onClick={() => toggleSort('user_name')}>
                    User Name{sortIndicator('user_name')}
                  </th>
                  <th style={{cursor:'pointer'}} onClick={() => toggleSort('sessionId')}>
                    Session ID{sortIndicator('sessionId')}
                  </th>
                  <th style={{cursor:'pointer'}} onClick={() => toggleSort('specialty')}>
                    Specialty{sortIndicator('specialty')}
                  </th>
                  <th style={{cursor:'pointer'}} onClick={() => toggleSort('started_at')}>
                    Start Date{sortIndicator('started_at')}
                  </th>
                  <th style={{cursor:'pointer'}} onClick={() => toggleSort('ended_at')}>
                    End Date{sortIndicator('ended_at')}
                  </th>
                  <th style={{cursor:'pointer'}} onClick={() => toggleSort('question_count')}>
                    Answered{sortIndicator('question_count')}
                  </th>
                  <th style={{cursor:'pointer'}} onClick={() => toggleSort('average_score')}>
                    Average Score{sortIndicator('average_score')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredAndSortedAttempts.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="no-data">
                      No interview attempts found.
                    </td>
                  </tr>
                ) : (
                  filteredAndSortedAttempts.map((attempt) => (
                    <tr key={attempt.id} style={{cursor:'pointer'}} onClick={() => setSelectedInterview(attempt)}>
                      <td data-label="User Name">{getUserName(attempt.user_id || attempt.uid)}</td>
                      <td data-label="Session ID">{attempt.sessionId || attempt.session_id || 'N/A'}</td>
                      <td data-label="Specialty">
                        {attempt.specialty ? 
                          attempt.specialty.split('-').map(word => 
                            word.charAt(0).toUpperCase() + word.slice(1)
                          ).join(' ') 
                          : 'N/A'
                        }
                      </td>
                      <td data-label="Start Date">
                        {attempt.started_at ? (
                          <span style={{display:'flex', flexDirection:'column'}}>
                            <span>{parseDate(attempt.started_at).toLocaleDateString()}</span>
                            <span>{parseDate(attempt.started_at).toLocaleTimeString()}</span>
                          </span>
                        ) : 'N/A'}
                      </td>
                      <td data-label="End Date">
                        {attempt.ended_at ? (
                          <span style={{display:'flex', flexDirection:'column'}}>
                            <span>{parseDate(attempt.ended_at).toLocaleDateString()}</span>
                            <span>{parseDate(attempt.ended_at).toLocaleTimeString()}</span>
                          </span>
                        ) : 'N/A'}
                      </td>
                      <td data-label="Questions Answered">
                        {attempt.question_count || 0}
                      </td>
                      <td data-label="Average Score">
                        {attempt.average_score ? attempt.average_score.toFixed(2) : 'N/A'}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Interview Details Modal */}
      {selectedInterview && (
        <SelectedInterviewModal
          interview={selectedInterview}
          user={getSelectedUser()}
          onClose={() => setSelectedInterview(null)}
        />
      )}

      {/* In-Progress Interviews Modal */}
      <InProgressInterviewsModal
        isOpen={showInProgressModal}
        onClose={() => setShowInProgressModal(false)}
        hideTestUsers={hideTestUsers}
      />
    </div>
  );
};

export default InterviewAnalysis;
